import React, { useState, useEffect, useCallback } from 'react';
import { Input } from '../../ui/Input/Input';
import { Select } from '../../ui/Select/Select';
import { Button } from '../../ui/Button/Button';
import { cn } from '../../../lib/cn';

/**
 * SearchBar component with search input and filter dropdowns
 *
 * @param {Object} props - Component props
 * @param {string} [props.className] - Additional CSS classes
 * @param {string} [props.searchValue=''] - Controlled search value
 * @param {Function} [props.onSearchChange] - Search change callback (value) => void
 * @param {string} [props.searchPlaceholder='Search...'] - Search input placeholder
 * @param {number} [props.debounceMs=300] - Debounce delay in milliseconds
 * @param {Array<{key: string, label: string, options: Array<{value: string, label: string}>, value?: string}>} [props.filters] - Filter definitions
 * @param {Function} [props.onFilterChange] - Filter change callback (filterKey, value) => void
 * @param {Function} [props.onClear] - Clear callback () => void
 * @param {boolean} [props.showClearButton=true] - Show clear button
 * @param {React.ReactNode} [props.actions] - Additional action buttons
 * @returns {JSX.Element}
 *
 * @example
 * <SearchBar
 *   searchValue={searchQuery}
 *   onSearchChange={(value) => setSearchQuery(value)}
 *   searchPlaceholder="Search services..."
 *   filters={[
 *     {
 *       key: 'category',
 *       label: 'Category',
 *       options: [
 *         { value: 'all', label: 'All Categories' },
 *         { value: 'apps', label: 'Apps & Pods' },
 *         { value: 'plugins', label: 'Plugins' }
 *       ],
 *       value: selectedCategory
 *     },
 *     {
 *       key: 'status',
 *       label: 'Status',
 *       options: [
 *         { value: 'all', label: 'All Statuses' },
 *         { value: 'active', label: 'Active' },
 *         { value: 'deprecated', label: 'Deprecated' }
 *       ],
 *       value: selectedStatus
 *     }
 *   ]}
 *   onFilterChange={(key, value) => {
 *     if (key === 'category') setSelectedCategory(value);
 *     if (key === 'status') setSelectedStatus(value);
 *   }}
 *   onClear={() => {
 *     setSearchQuery('');
 *     setSelectedCategory('all');
 *     setSelectedStatus('all');
 *   }}
 * />
 */
export function SearchBar({
  className,
  searchValue = '',
  onSearchChange,
  searchPlaceholder = 'Search...',
  debounceMs = 300,
  filters = [],
  onFilterChange,
  onClear,
  showClearButton = true,
  actions,
}) {
  const [localSearchValue, setLocalSearchValue] = useState(searchValue);

  // Sync local state with prop value
  useEffect(() => {
    setLocalSearchValue(searchValue);
  }, [searchValue]);

  // Debounced search
  useEffect(() => {
    if (debounceMs === 0) {
      onSearchChange?.(localSearchValue);
      return;
    }

    const timer = setTimeout(() => {
      onSearchChange?.(localSearchValue);
    }, debounceMs);

    return () => clearTimeout(timer);
  }, [localSearchValue, debounceMs, onSearchChange]);

  const handleSearchInput = (e) => {
    setLocalSearchValue(e.target.value);
  };

  const handleFilterChange = (filterKey, e) => {
    const value = e.detail.selectedOption.value;
    onFilterChange?.(filterKey, value);
  };

  const handleClear = () => {
    setLocalSearchValue('');
    onClear?.();
  };

  const hasActiveFilters = searchValue || filters.some((f) => f.value && f.value !== 'all');

  return (
    <div className={cn('space-y-3', className)}>
      {/* Main search row */}
      <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3">
        {/* Search input */}
        <div className="flex-1">
          <Input
            placeholder={searchPlaceholder}
            value={localSearchValue}
            onInput={handleSearchInput}
            icon="search"
            className="w-full"
          />
        </div>

        {/* Clear button */}
        {showClearButton && hasActiveFilters && (
          <Button design="Transparent" onClick={handleClear}>
            Clear
          </Button>
        )}

        {/* Additional actions */}
        {actions && (
          <div className="flex items-center gap-2">{actions}</div>
        )}
      </div>

      {/* Filters row */}
      {filters.length > 0 && (
        <div className="flex flex-wrap items-center gap-3">
          {filters.map((filter) => (
            <div key={filter.key} className="flex items-center gap-2 min-w-[180px]">
              <span className="text-sm text-gray-600 whitespace-nowrap">
                {filter.label}:
              </span>
              <Select
                value={filter.value || filter.options[0]?.value}
                onChange={(e) => handleFilterChange(filter.key, e)}
                options={filter.options}
                className="flex-1"
              />
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
